<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <!-- 头部 -->
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-calendar-event me-2"></i>
          {{ isMyBooking ? 'My Booking' : 'Event Preview' }}
        </h5>
        <button type="button" class="btn-close" @click="$emit('close')"></button>
      </div>

      <!-- 内容 -->
      <div class="modal-body">
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <div v-else-if="eventDetails" class="event-preview">
          <!-- 活动标题 -->
          <h4 class="event-title mb-3">{{ eventDetails.title }}</h4>

          <!-- 分类标签 -->
          <div class="mb-3">
            <span class="badge" :style="{ backgroundColor: getCategoryColor(eventDetails.category) }">
              {{ eventDetails.category }}
            </span>
            <span v-if="isFull" class="badge bg-danger ms-2">FULL</span>
            <span v-if="isPast" class="badge bg-secondary ms-2">Past Event</span>
          </div>

          <!-- 详细信息 -->
          <div class="details-grid">
            <div class="detail-item">
              <i class="bi bi-calendar3"></i>
              <div>
                <label>Date & Time</label>
                <p>{{ formatDateTime(eventDetails.event_date) }}</p>
              </div>
            </div>

            <div class="detail-item">
              <i class="bi bi-geo-alt-fill"></i>
              <div>
                <label>Location</label>
                <p>{{ eventDetails.location || 'TBA' }}</p>
              </div>
            </div>

            <div class="detail-item">
              <i class="bi bi-people-fill"></i>
              <div>
                <label>Participants</label>
                <p>
                  {{ currentParticipants }} / {{ maxParticipants }}
                  <span v-if="remainingSlots > 0" class="text-success ms-2">
                    ({{ remainingSlots }} slots left)
                  </span>
                </p>
              </div>
            </div>

            <div v-if="eventDetails.description" class="detail-item full-width">
              <i class="bi bi-card-text"></i>
              <div>
                <label>Description</label>
                <p>{{ eventDetails.description }}</p>
              </div>
            </div>

            <!-- 我的预约信息 -->
            <div v-if="isMyBooking && bookingDetails" class="detail-item full-width booking-info">
              <i class="bi bi-check-circle-fill text-success"></i>
              <div>
                <label>Booking Status</label>
                <p class="mb-2">
                  <span class="badge bg-success">Confirmed</span>
                </p>
                <small class="text-muted">
                  Booked on {{ formatDate(bookingDetails.created_at) }}
                </small>
                <p v-if="bookingDetails.notes" class="mt-2">
                  <strong>Notes:</strong> {{ bookingDetails.notes }}
                </p>
              </div>
            </div>
          </div>

          <!-- 警告信息 -->
          <div v-if="isFull && !isMyBooking" class="alert alert-danger mt-3">
            <i class="bi bi-exclamation-triangle"></i>
            This event has reached maximum capacity.
          </div>

          <div v-if="isPast" class="alert alert-warning mt-3">
            <i class="bi bi-clock-history"></i>
            This event has already ended.
          </div>

          <div v-if="isMyBooking && !canCancelBooking" class="alert alert-warning mt-3">
            <i class="bi bi-info-circle"></i>
            Cannot cancel booking less than 12 hours before the event.
          </div>
        </div>
      </div>

      <!-- 底部按钮 -->
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" @click="$emit('close')">Close</button>

        <!-- 我的预约操作 -->
        <template v-if="isMyBooking">
          <button
            v-if="canCancelBooking"
            type="button"
            class="btn btn-danger"
            @click="handleCancelBooking"
          >
            <i class="bi bi-x-circle"></i> Cancel Booking
          </button>
        </template>

        <!-- 活动操作 -->
        <template v-else>
          <button type="button" class="btn btn-primary" @click="goToEventDetail">
            <i class="bi bi-box-arrow-up-right"></i> View Details
          </button>

          <button
            v-if="!isFull && !isPast && isAuthenticated"
            type="button"
            class="btn btn-success"
            @click="goToEventDetail"
          >
            <i class="bi bi-calendar-check"></i> Book Now
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useFirebaseAuth } from '@/composables/useFirebaseAuth'
import { useConfirm } from '@/composables/useConfirm'
import { useToast } from '@/composables/useToast'
import { getEventById } from '@/services/eventService'
import { canCancelBooking as canCancelBookingFn } from '@/services/bookingService'

export default {
  name: 'EventPreviewModal',
  props: {
    eventId: {
      type: String,
      required: true
    },
    isMyBooking: {
      type: Boolean,
      default: false
    },
    bookingDetails: {
      type: Object,
      default: null
    }
  },
  emits: ['close', 'cancel-booking'],
  setup(props, { emit }) {
    const router = useRouter()
    const { isAuthenticated } = useFirebaseAuth()
    const { confirm } = useConfirm()
    const toast = useToast()

    const loading = ref(false)
    const eventDetails = ref(null)

    const categoryColors = {
      'Health Activities': '#F06292',
      Workshop: '#BA68C8',
      Counseling: '#64B5F6',
      'Community Event': '#4DB6AC'
    }

    const currentParticipants = computed(() => {
      if (!eventDetails.value) return 0
      return eventDetails.value.current_participants || eventDetails.value.participants || 0
    })

    const maxParticipants = computed(() => {
      if (!eventDetails.value) return 0
      return eventDetails.value.max_participants || eventDetails.value.capacity || 0
    })

    const isFull = computed(() => {
      if (!eventDetails.value) return false
      return currentParticipants.value >= maxParticipants.value
    })

    const isPast = computed(() => {
      if (!eventDetails.value || !eventDetails.value.event_date) return false
      const eventDate =
        eventDetails.value.event_date?.toDate?.() || new Date(eventDetails.value.event_date)
      return eventDate < new Date()
    })

    const remainingSlots = computed(() => {
      if (!eventDetails.value) return 0
      return maxParticipants.value - currentParticipants.value
    })

    const canCancelBooking = computed(() => {
      if (!props.isMyBooking || !props.bookingDetails) return false
      return canCancelBookingFn(props.bookingDetails.start_time)
    })

    const getCategoryColor = (category) => {
      return categoryColors[category] || '#9E9E9E'
    }

    const formatDateTime = (date) => {
      if (!date) return 'N/A'
      const d = date?.toDate ? date.toDate() : new Date(date)
      return d.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatDate = (date) => {
      if (!date) return 'N/A'
      const d = date?.toDate ? date.toDate() : new Date(date)
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const loadEventDetails = async () => {
      loading.value = true
      try {
        eventDetails.value = await getEventById(props.eventId)
      } catch (error) {
        console.error('Error loading event details:', error)
        toast.error('Failed to load event details')
      } finally {
        loading.value = false
      }
    }

    const goToEventDetail = () => {
      router.push({
        name: 'EventDetail',
        params: { id: props.eventId },
        query: { from: 'calendar' }
      })
    }

    const handleCancelBooking = async () => {
      const confirmed = await confirm(
        'Are you sure you want to cancel this booking? This action cannot be undone.',
        {
          title: 'Cancel Booking',
          confirmText: 'Yes, Cancel',
          confirmButtonClass: 'btn-danger'
        }
      )

      if (confirmed) {
        emit('cancel-booking', props.bookingDetails.id)
      }
    }

    watch(
      () => props.eventId,
      () => {
        if (props.eventId) {
          loadEventDetails()
        }
      },
      { immediate: true }
    )

    return {
      loading,
      eventDetails,
      isAuthenticated,
      currentParticipants,
      maxParticipants,
      isFull,
      isPast,
      remainingSlots,
      canCancelBooking,
      getCategoryColor,
      formatDateTime,
      formatDate,
      goToEventDetail,
      handleCancelBooking
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
}

.btn-close {
  filter: brightness(0) invert(1);
  opacity: 0.8;
}

.btn-close:hover {
  opacity: 1;
}

.modal-body {
  padding: 1.5rem;
}

.event-preview {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.event-title {
  color: #333;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.details-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

.detail-item {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #667eea;
}

.detail-item.full-width {
  grid-column: 1 / -1;
}

.detail-item.booking-info {
  background-color: #e8f5e9;
  border-left-color: #4caf50;
}

.detail-item i {
  font-size: 1.5rem;
  color: #667eea;
  flex-shrink: 0;
  margin-top: 0.25rem;
}

.detail-item.booking-info i {
  color: #4caf50;
}

.detail-item label {
  font-weight: 600;
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
  display: block;
}

.detail-item p {
  margin: 0;
  font-size: 1rem;
  color: #212529;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 0 0 12px 12px;
}

@media (max-width: 576px) {
  .modal-content {
    margin: 0;
    border-radius: 0;
    max-height: 100vh;
  }

  .modal-header {
    border-radius: 0;
  }

  .modal-footer {
    flex-direction: column;
    border-radius: 0;
  }

  .modal-footer button {
    width: 100%;
  }
}
</style>
